import React, { useState, useMemo, useEffect } from "react";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl, fmtDate, filterByMonth, sumValor, fmtMonthLong } from "./_shared/utils";
import { catById } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import { CartaoModal, CartaoTxModal, ImportCsvModal } from "./_shared/CartaoModals";
import { ImportPdfModal } from "./_shared/ImportPdfModal";
import ConfirmDialog from "./_shared/ConfirmDialog";
import {
  CreditCard, ChevronDown, ChevronUp, Plus, Pencil, Trash2,
  Upload, FilePlus2, FileText, Search, Check, X,
} from "lucide-react";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const STATUS_CFG = {
  paga:            { label: "Paga",             icon: "✅", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
  em_aberto:       { label: "Em aberto",        icon: "⏳", cls: "text-amber-400   border-amber-500/30   bg-amber-500/10"   },
  vencida:         { label: "Vencida",          icon: "❌", cls: "text-rose-400    border-rose-500/30    bg-rose-500/10"    },
  sem_lancamentos: { label: "Sem lançamentos",  icon: "⬜", cls: "text-slate-500   border-slate-700      bg-transparent"    },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const todayYM = () => new Date().toISOString().slice(0, 7);

const getMonthStatus = (card, monthKey, curYM) => {
  const txs = (card.transacoes || []).filter((t) => (t.data || "").startsWith(monthKey));
  if (txs.length === 0) return "sem_lancamentos";
  if (card.faturasPagas?.[monthKey]) return "paga";
  if (monthKey < curYM) return "vencida";
  return "em_aberto";
};

const fmtMonthKey = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${MONTHS_SHORT[parseInt(m, 10) - 1]}/${y}`;
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const TONE_CLS = {
  current: "from-blue-600 to-blue-800",
  pending: "from-amber-500 to-orange-600",
  future:  "from-emerald-500 to-emerald-700",
};

const StatCard = ({ label, value, tone, subtext, onClick, active, testid }) => (
  <div
    className={`relative rounded-2xl p-5 bg-gradient-to-br ${TONE_CLS[tone]} text-white shadow-lg
      ${onClick ? "cursor-pointer hover:opacity-90 transition-opacity select-none" : ""}`}
    onClick={onClick}
    data-testid={testid}
  >
    <div className="text-xs uppercase tracking-wider font-semibold opacity-90">{label}</div>
    <div className="mt-2 text-2xl md:text-3xl font-display font-bold tabular-nums">{brl(value)}</div>
    {subtext && <div className="mt-1 text-xs opacity-80">{subtext}</div>}
    {onClick && (
      <div className="absolute top-4 right-4 opacity-70">
        {active ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </div>
    )}
  </div>
);

// ─── LimitBar ─────────────────────────────────────────────────────────────────

const LimitBar = ({ limite, usado }) => {
  if (!limite || limite <= 0) return null;
  const disponivel = Math.max(0, limite - usado);
  const pct = Math.min(100, Math.round((usado / limite) * 100));
  const barColor = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  const pctColor = pct >= 90 ? "text-rose-400" : pct >= 70 ? "text-amber-400" : "text-emerald-400";

  return (
    <div className="rounded-2xl p-5 bg-slate-900 border border-slate-800">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider font-semibold text-slate-400">Limite do cartão</span>
        <span className={`text-sm font-bold ${pctColor}`}>{pct}% do limite utilizado</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Limite total</div>
          <div className="font-bold text-white tabular-nums mt-0.5">{brl(limite)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Utilizado</div>
          <div className={`font-bold tabular-nums mt-0.5 ${pct >= 70 ? "text-amber-400" : "text-white"}`}>{brl(usado)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Disponível</div>
          <div className="font-bold text-emerald-400 tabular-nums mt-0.5">{brl(disponivel)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Cartoes() {
  const { data, removeCartao, removeCartaoTx, updateCartao } = useAppData();
  const cartoes = data.cartoes || [];
  const ym = data.selectedMonth;

  const [selected, setSelected] = useState(cartoes[0]?.id || null);
  const [open, setOpen] = useState(false);
  const [cartaoModal, setCartaoModal] = useState({ open: false, item: null });
  const [txModal, setTxModal] = useState({ open: false, item: null });
  const [csvOpen, setCsvOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [importDropOpen, setImportDropOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState({ open: false, type: null, id: null, desc: "" });
  const [confirmClear, setConfirmClear] = useState(false);

  // expanded stat panels
  const [pendentesOpen, setPendentesOpen] = useState(false);
  const [futurasOpen, setFuturasOpen] = useState(false);

  // table filters
  const [activeYM, setActiveYM] = useState(null);
  const [searchDesc, setSearchDesc] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const card = useMemo(() => cartoes.find((c) => c.id === selected) || cartoes[0], [cartoes, selected]);
  const year  = parseInt(ym.split("-")[0], 10);
  const curYM = todayYM();
  const efectiveYM = activeYM || ym;

  // reset table filters on cartão/month change
  useEffect(() => {
    setActiveYM(null);
    setSearchDesc("");
    setStatusFilter("all");
    setPendentesOpen(false);
    setFuturasOpen(false);
  }, [ym, selected]);

  // ── stat totals (global ym) ──────────────────────────────────────────────
  const txMes = useMemo(() =>
    card ? filterByMonth(card.transacoes || [], ym).sort((a, b) => (a.data || "").localeCompare(b.data || "")) : [],
  [card, ym]);

  const totalMes = sumValor(txMes);

  const totalPendentes = useMemo(() =>
    card ? (card.transacoes || []).filter((t) => (t.data || "") < `${ym}-01`).reduce((s, t) => s + Number(t.valor || 0), 0) : 0,
  [card, ym]);

  const totalFuturas = useMemo(() =>
    card ? (card.transacoes || []).filter((t) => (t.data || "") > `${ym}-31`).reduce((s, t) => s + Number(t.valor || 0), 0) : 0,
  [card, ym]);

  // ── breakdown panels ─────────────────────────────────────────────────────
  const pendentesPorMes = useMemo(() => {
    if (!card) return [];
    const by = {};
    (card.transacoes || []).filter((t) => (t.data || "") < `${ym}-01`).forEach((t) => {
      const mk = (t.data || "").slice(0, 7);
      if (!by[mk]) by[mk] = { key: mk, total: 0 };
      by[mk].total += Number(t.valor || 0);
    });
    return Object.values(by).sort((a, b) => b.key.localeCompare(a.key)).slice(0, 12);
  }, [card, ym]);

  const futurasPorMes = useMemo(() => {
    if (!card) return [];
    const by = {};
    (card.transacoes || []).filter((t) => (t.data || "") > `${ym}-31`).forEach((t) => {
      const mk = (t.data || "").slice(0, 7);
      if (!by[mk]) by[mk] = { key: mk, total: 0 };
      by[mk].total += Number(t.valor || 0);
    });
    return Object.values(by).sort((a, b) => a.key.localeCompare(b.key)).slice(0, 12);
  }, [card, ym]);

  // ── 12-month grid ────────────────────────────────────────────────────────
  const meses12 = useMemo(() => {
    if (!card) return [];
    return Array.from({ length: 12 }, (_, i) => {
      const m = String(i + 1).padStart(2, "0");
      const key = `${year}-${m}`;
      const txs = (card.transacoes || []).filter((t) => (t.data || "").startsWith(key));
      const total = txs.reduce((s, t) => s + Number(t.valor || 0), 0);
      const status = getMonthStatus(card, key, curYM);
      return { key, name: MONTHS_SHORT[i], total, status };
    });
  }, [card, year, curYM]);

  // ── limit bar ────────────────────────────────────────────────────────────
  const totalUtilizado = useMemo(() => {
    if (!card) return 0;
    return (card.transacoes || [])
      .filter((t) => !card.faturasPagas?.[(t.data || "").slice(0, 7)])
      .reduce((s, t) => s + Number(t.valor || 0), 0);
  }, [card]);

  // ── filtered table (efectiveYM + search + status) ──────────────────────
  const txFiltered = useMemo(() => {
    if (!card) return [];
    let txs = filterByMonth(card.transacoes || [], efectiveYM)
      .sort((a, b) => (a.data || "").localeCompare(b.data || ""));
    if (searchDesc.trim())
      txs = txs.filter((t) => t.desc.toLowerCase().includes(searchDesc.toLowerCase()));
    if (statusFilter === "pago")
      txs = txs.filter((t) => card.faturasPagas?.[(t.data || "").slice(0, 7)]);
    if (statusFilter === "pendente")
      txs = txs.filter((t) => !card.faturasPagas?.[(t.data || "").slice(0, 7)]);
    return txs;
  }, [card, efectiveYM, searchDesc, statusFilter]);

  // ── vencimento label ─────────────────────────────────────────────────────
  const venceLabel = card ? `Vence ${String(card.vencimento).padStart(2, "0")}/${ym.slice(5, 7)}` : "";

  // ── future parcel info ────────────────────────────────────────────────────
  const getParcelRest = (t) => {
    const m = String(t.parcelas || "1/1").match(/^(\d+)\/(\d+)$/);
    if (!m) return null;
    const cur = parseInt(m[1], 10), total = parseInt(m[2], 10);
    if (total <= 1 || cur >= total) return null;
    const rem = total - cur;
    const valorParcela = Number(t.valor || 0); // valor stored is already per-installment
    return { rem, value: valorParcela, total: rem * valorParcela };
  };

  // ── handlers ─────────────────────────────────────────────────────────────
  const toggleFaturaPaga = (monthKey, e) => {
    e?.stopPropagation();
    if (!card) return;
    const faturasPagas = { ...(card.faturasPagas || {}), [monthKey]: !card.faturasPagas?.[monthKey] };
    updateCartao(card.id, { faturasPagas });
    toast.success(faturasPagas[monthKey] ? "Fatura marcada como paga ✅" : "Fatura marcada como pendente ⏳");
  };

  const removeThisCartao = () => {
    if (!card) return;
    setConfirmDel({ open: true, type: "cartao", id: card.id, desc: card.nome });
  };

  const askRemoveTx = (txId, desc) => {
    if (!card) return;
    setConfirmDel({ open: true, type: "tx", id: txId, desc: desc || "Transação" });
  };

  const doConfirmRemove = () => {
    if (confirmDel.type === "cartao") {
      removeCartao(confirmDel.id);
      setSelected(cartoes.filter((c) => c.id !== confirmDel.id)[0]?.id || null);
      toast.success("Cartão removido");
    } else if (confirmDel.type === "tx" && card) {
      removeCartaoTx(card.id, confirmDel.id);
      toast.success(`"${confirmDel.desc}" removida`);
    }
  };

  const clearAllTx = () => {
    if (!card) return;
    if (!confirmClear) {
      setConfirmClear(true);
      toast.warning("Clique novamente para confirmar a remoção de TODAS as transações!");
      setTimeout(() => setConfirmClear(false), 3000);
      return;
    }
    updateCartao(card.id, { transacoes: [] });
    setConfirmClear(false);
    toast.success("Todas as transações foram removidas!");
  };

  // ─── Empty state ──────────────────────────────────────────────────────────
  if (!card) {
    return (
      <AppLayout>
        <PageHeader title="Despesas no Cartão de Crédito" subtitle="Nenhum cartão cadastrado" testid="cartoes" />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center">
          <CreditCard className="h-16 w-16 mx-auto text-slate-600" />
          <h2 className="mt-4 font-display text-2xl font-bold">Você ainda não tem cartões</h2>
          <p className="mt-2 text-slate-400">Adicione seu primeiro cartão para registrar compras e parcelas.</p>
          <button
            onClick={() => setCartaoModal({ open: true, item: null })}
            className="mt-6 monyx-btn-primary text-white rounded-full h-11 px-6 inline-flex items-center gap-2"
            data-testid="empty-add-cartao"
          >
            <Plus className="h-4 w-4" /> Adicionar cartão
          </button>
        </div>
        <CartaoModal open={cartaoModal.open} onOpenChange={(o) => setCartaoModal({ ...cartaoModal, open: o })} item={cartaoModal.item} />
      </AppLayout>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <PageHeader title="Despesas no Cartão de Crédito" subtitle={`${fmtMonthLong(ym)} · ${brl(totalMes)} no mês`} testid="cartoes" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 space-y-6">

        {/* ── 1. Stat Cards ────────────────────────────────────────────── */}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard
            testid="card-atual"
            label="Atual (no mês)"
            value={totalMes}
            tone="current"
            subtext={venceLabel}
          />
          <StatCard
            testid="card-pendente"
            label="Pendente (anteriores)"
            value={totalPendentes}
            tone="pending"
            onClick={totalPendentes > 0 ? () => { setPendentesOpen((v) => !v); setFuturasOpen(false); } : undefined}
            active={pendentesOpen}
          />
          <StatCard
            testid="card-futuro"
            label="Futuro (próximos meses)"
            value={totalFuturas}
            tone="future"
            onClick={totalFuturas > 0 ? () => { setFuturasOpen((v) => !v); setPendentesOpen(false); } : undefined}
            active={futurasOpen}
          />
        </div>

        {/* ── Pendentes breakdown panel ─────────────────────────────────── */}
        {pendentesOpen && pendentesPorMes.length > 0 && (
          <div className="rounded-2xl bg-slate-900 border border-amber-500/20 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-amber-400 mb-3">
              Meses com pendências
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {pendentesPorMes.map(({ key, total }) => (
                <button
                  key={key}
                  onClick={() => { setActiveYM(key); setPendentesOpen(false); }}
                  className="text-left px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 hover:border-amber-500/40 transition"
                >
                  <div className="text-xs font-semibold text-slate-400">{fmtMonthKey(key)}</div>
                  <div className="mt-1 font-bold text-amber-300 tabular-nums text-sm">{brl(total)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Futuras breakdown panel ───────────────────────────────────── */}
        {futurasOpen && futurasPorMes.length > 0 && (
          <div className="rounded-2xl bg-slate-900 border border-emerald-500/20 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-400 mb-3">
              Distribuição futura por mês
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {futurasPorMes.map(({ key, total }) => (
                <button
                  key={key}
                  onClick={() => { setActiveYM(key); setFuturasOpen(false); }}
                  className="text-left px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 hover:border-emerald-500/40 transition"
                >
                  <div className="text-xs font-semibold text-slate-400">{fmtMonthKey(key)}</div>
                  <div className="mt-1 font-bold text-emerald-300 tabular-nums text-sm">{brl(total)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── 2. Limit Bar ─────────────────────────────────────────────── */}
        <LimitBar limite={Number(card.limite || 0)} usado={totalUtilizado} />

        {/* ── Card selector + actions ───────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <button
              onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 transition"
              data-testid="card-selector"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 grid place-items-center">
                  <CreditCard className="h-5 w-5 text-emerald-300" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-semibold truncate">{card.nome}</div>
                  <div className="text-xs text-slate-400 tabular-nums">
                    **** {card.final} · fecha dia {card.fechamento} · vence {card.vencimento}
                    {card.limite ? ` · Limite ${brl(card.limite)}` : ""}
                  </div>
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl bg-slate-900 border border-slate-800 shadow-xl z-20 overflow-hidden">
                {cartoes.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setSelected(c.id); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-800 transition text-left ${c.id === selected ? "bg-slate-800/60" : ""}`}
                    data-testid={`card-opt-${c.id}`}
                  >
                    <CreditCard className="h-4 w-4 text-emerald-300" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{c.nome}</div>
                      <div className="text-xs text-slate-400">**** {c.final}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setCartaoModal({ open: true, item: null })}
            className="rounded-full h-10 px-4 text-sm font-semibold border border-slate-700 hover:border-emerald-500/40 hover:text-emerald-300 transition inline-flex items-center gap-2"
            data-testid="btn-novo-cartao"
          >
            <FilePlus2 className="h-4 w-4" /> Novo cartão
          </button>
          <button
            onClick={() => setCartaoModal({ open: true, item: card })}
            className="rounded-full h-10 px-4 text-sm font-semibold border border-slate-700 hover:border-emerald-500/40 hover:text-emerald-300 transition inline-flex items-center gap-2"
            data-testid="btn-editar-cartao"
          >
            <Pencil className="h-4 w-4" /> Editar cartão
          </button>
          <button
            onClick={removeThisCartao}
            className="rounded-full h-10 px-4 text-sm font-semibold border border-rose-700 text-rose-300 hover:bg-rose-500/10 transition inline-flex items-center gap-2"
            data-testid="btn-remover-cartao"
          >
            <Trash2 className="h-4 w-4" /> Remover
          </button>
        </div>

        {/* ── 3. 12-month invoice cards ─────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-lg">Faturas {year}</h3>
            {activeYM && (
              <button
                onClick={() => setActiveYM(null)}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5 transition"
              >
                <X className="h-3.5 w-3.5" /> Limpar filtro
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {meses12.map(({ key, name, total, status }) => {
              const cfg = STATUS_CFG[status];
              const isActive = key === efectiveYM;
              const isGlobalMonth = key === ym;
              return (
                <div
                  key={key}
                  className={`relative rounded-xl border p-3 cursor-pointer transition select-none
                    ${cfg.cls}
                    ${isActive ? "ring-2 ring-emerald-400/60" : ""}
                    ${!isActive && isGlobalMonth ? "ring-2 ring-blue-400/40" : ""}`}
                  onClick={() => setActiveYM(key === activeYM ? null : key)}
                  data-testid={`month-card-${key}`}
                >
                  <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{name}</div>
                  {total > 0
                    ? <div className="mt-1 text-sm font-bold tabular-nums">{brl(total)}</div>
                    : <div className="mt-1 text-xs opacity-50">—</div>
                  }
                  <div className="mt-1 text-[10px] opacity-70 leading-tight">{cfg.icon} {cfg.label}</div>

                  {/* toggle paid button */}
                  {status !== "sem_lancamentos" && (
                    <button
                      onClick={(e) => toggleFaturaPaga(key, e)}
                      title={status === "paga" ? "Marcar como pendente" : "Marcar como paga"}
                      className={`absolute top-1.5 right-1.5 h-5 w-5 rounded-full grid place-items-center transition
                        ${status === "paga"
                          ? "bg-emerald-500/30 text-emerald-300 hover:bg-rose-500/20 hover:text-rose-300"
                          : "bg-slate-700/80 text-slate-400 hover:bg-emerald-500/20 hover:text-emerald-300"}`}
                    >
                      <Check className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── 4. Tx actions + search/filters ───────────────────────────── */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTxModal({ open: true, item: null })}
              className="monyx-btn-primary text-white rounded-full h-10 px-5 text-sm font-semibold inline-flex items-center gap-2"
              data-testid="btn-nova-tx"
            >
              <Plus className="h-4 w-4" /> Nova transação
            </button>

            {/* Importar Fatura dropdown */}
            <div className="relative">
              <button
                onClick={() => setImportDropOpen((v) => !v)}
                className="rounded-full h-10 px-5 text-sm font-semibold border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition inline-flex items-center gap-2"
                data-testid="btn-importar-fatura"
              >
                <Upload className="h-4 w-4" /> Importar Fatura
                <ChevronDown className={`h-3.5 w-3.5 transition ${importDropOpen ? "rotate-180" : ""}`} />
              </button>
              {importDropOpen && (
                <div className="absolute top-full left-0 mt-2 rounded-xl bg-slate-900 border border-slate-800 shadow-xl z-20 min-w-[200px] overflow-hidden">
                  <button
                    onClick={() => { setPdfOpen(true); setImportDropOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800 transition text-left text-sm"
                    data-testid="btn-import-pdf"
                  >
                    <FileText className="h-4 w-4 text-emerald-300 shrink-0" />
                    <div>
                      <div className="font-semibold">PDF / OFX / Imagem</div>
                      <div className="text-[11px] text-slate-500">Análise automática por IA</div>
                    </div>
                  </button>
                  <button
                    onClick={() => { setCsvOpen(true); setImportDropOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800 transition text-left text-sm border-t border-slate-800"
                    data-testid="btn-import-csv"
                  >
                    <Upload className="h-4 w-4 text-blue-300 shrink-0" />
                    <div>
                      <div className="font-semibold">CSV / Texto</div>
                      <div className="text-[11px] text-slate-500">Dados em colunas separadas</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {(card.transacoes?.length || 0) > 0 && (
              <button
                onClick={clearAllTx}
                className={`rounded-full h-10 px-5 text-sm font-semibold border transition inline-flex items-center gap-2
                  ${confirmClear
                    ? "border-rose-500 bg-rose-500/20 text-rose-200 animate-pulse"
                    : "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"}`}
                data-testid="btn-limpar-tx"
              >
                <Trash2 className="h-4 w-4" />
                {confirmClear ? "⚠️ Confirmar remoção!" : `Limpar tudo (${card.transacoes.length})`}
              </button>
            )}
          </div>

          {/* Search + status filter */}
          <div className="flex flex-wrap gap-2 items-center">
            {activeYM && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs font-semibold text-emerald-300">
                <span>{fmtMonthKey(activeYM)}</span>
                <button onClick={() => setActiveYM(null)} className="hover:text-white transition ml-0.5">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
              <input
                value={searchDesc}
                onChange={(e) => setSearchDesc(e.target.value)}
                placeholder="Buscar por descrição..."
                className="w-full h-9 pl-8 pr-3 rounded-full bg-slate-900 border border-slate-800 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 rounded-full bg-slate-900 border border-slate-800 text-sm text-slate-300 focus:outline-none focus:border-emerald-500/40"
            >
              <option value="all">Todos os status</option>
              <option value="pago">✅ Faturas pagas</option>
              <option value="pendente">⏳ Faturas pendentes</option>
            </select>
          </div>
        </div>

        {/* ── 4. Transactions table ─────────────────────────────────────── */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          {/* Desktop header */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-800">
            <div className="col-span-2">Data</div>
            <div className="col-span-3">Descrição</div>
            <div className="col-span-2">Categoria</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-center">Parcelas</div>
            <div className="col-span-1 text-center">Restantes</div>
            <div className="col-span-1 text-right">Valor</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>

          <div className="divide-y divide-slate-800">
            {txFiltered.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">
                {searchDesc || statusFilter !== "all"
                  ? "Nenhuma transação encontrada com os filtros aplicados."
                  : `Nenhuma transação em ${fmtMonthLong(efectiveYM)}. Use Nova transação ou Importar Fatura.`
                }
              </div>
            ) : txFiltered.map((t) => {
              const cat = catById(t.cat, data.customCategories);
              const monthKey = (t.data || "").slice(0, 7);
              const isPago = !!(card.faturasPagas?.[monthKey]);
              const parcelRest = getParcelRest(t);

              return (
                <div key={t.id} className="px-4 md:px-5 py-3 hover:bg-slate-800/30 transition">
                  {/* DESKTOP */}
                  <div className="hidden md:grid grid-cols-12 items-center text-sm">
                    <div className="col-span-2 text-slate-400 text-xs tabular-nums">{fmtDate(t.data)}</div>
                    <div className="col-span-3 text-white truncate" title={t.desc}>{t.desc}</div>
                    <div className="col-span-2">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.bg} ${cat.text}`}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                        {cat.name}
                      </span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isPago ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                        {isPago ? "✅ Pago" : "⏳ Pend."}
                      </span>
                    </div>
                    <div className="col-span-1 text-center text-xs text-slate-300">{t.parcelas}</div>
                    <div className="col-span-1 text-center text-xs text-slate-400 tabular-nums">
                      {parcelRest
                        ? <span title={`${parcelRest.rem} parcelas restantes · ${brl(parcelRest.value)} cada · total ${brl(parcelRest.total)}`}>{parcelRest.rem}x · {brl(parcelRest.value)}</span>
                        : "—"}
                    </div>
                    <div className="col-span-1 text-right font-semibold tabular-nums">{brl(t.valor)}</div>
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      <button
                        onClick={() => setTxModal({ open: true, item: t })}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-emerald-300 transition"
                        data-testid={`tx-${t.id}-edit`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => askRemoveTx(t.id, t.desc)}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-rose-300 transition"
                        data-testid={`tx-${t.id}-del`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* MOBILE */}
                  <div className="md:hidden flex flex-col gap-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-white font-medium truncate flex-1 min-w-0">{t.desc}</div>
                      <div className="shrink-0 font-bold tabular-nums text-white">{brl(t.valor)}</div>
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.bg} ${cat.text}`}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isPago ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                          {isPago ? "✅ Pago" : "⏳ Pend."}
                        </span>
                        <span className="text-[11px] text-slate-300 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700">{t.parcelas}</span>
                        <span className="text-[11px] text-slate-400 tabular-nums">{fmtDate(t.data)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setTxModal({ open: true, item: t })}
                          className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-emerald-300 transition"
                          data-testid={`tx-${t.id}-edit-m`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => askRemoveTx(t.id, t.desc)}
                          className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-rose-300 transition"
                          data-testid={`tx-${t.id}-del-m`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {parcelRest && (
                      <div className="text-[11px] text-slate-500 tabular-nums">
                        {parcelRest.rem} parcelas restantes · {brl(parcelRest.value)} cada · {brl(parcelRest.total)} no futuro
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* ── Modals ── */}
      <CartaoModal
        open={cartaoModal.open}
        onOpenChange={(o) => setCartaoModal({ ...cartaoModal, open: o })}
        item={cartaoModal.item}
      />
      <CartaoTxModal
        open={txModal.open}
        onOpenChange={(o) => setTxModal({ ...txModal, open: o })}
        item={txModal.item}
        cartaoId={card.id}
      />
      <ImportCsvModal open={csvOpen} onOpenChange={setCsvOpen} cartaoId={card.id} />
      <ImportPdfModal open={pdfOpen} onOpenChange={setPdfOpen} cartaoId={card.id} />

      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(v) => setConfirmDel((s) => ({ ...s, open: v }))}
        title={confirmDel.type === "cartao" ? "Remover cartão?" : "Remover transação?"}
        description={
          confirmDel.type === "cartao"
            ? `Deseja remover o cartão "${confirmDel.desc}"? Todas as transações serão perdidas.`
            : `Deseja remover "${confirmDel.desc}"? Essa ação não pode ser desfeita.`
        }
        onConfirm={doConfirmRemove}
      />
    </AppLayout>
  );
}
